import {DataTableColumn} from 'mantine-datatable';
import {I18n} from 'aws-amplify/utils';
import {
    Badge,
    Group,
    Text,
    Tooltip,
} from '@mantine/core';

import {DnsRecord} from '../types';
import {
    hasReportedValue,
    recordValueText,
    ttlText,
} from '../recordValue';

// FNP-1192 — THE COLUMNS. The value column is the point of the ticket.
export const useDnsRecordsTableColumns = () => {

    // EVERY column is sortable (the module checklist is explicit), through the
    // shared comparator.
    const columns: DataTableColumn<DnsRecord>[] = [
        {
            accessor: 'name',
            sortable: true,
            title: I18n.get('Name'),
            // FNP-1284 - NO WIDTH ON THE FLEXIBLE COLUMN. Leave it that way.
            //
            // A column with no width takes the space the fixed columns leave. A grid
            // fraction here made it content-size instead: the fixed columns are
            // committed first and this one grew to fit its longest value, pushing the
            // table past its container and raising a horizontal scrollbar.
            //
            // `minWidth` in px is fine if a floor is ever wanted. A grid fraction is
            // not: mantine-datatable puts the value in an inline style on a real
            // <table> cell, where `fr` is not a valid unit.
            render: (item: DnsRecord) => (
                <Text size={'sm'}>{item.name}</Text>
            ),
        },
        {
            accessor: 'type',
            sortable: true,
            title: I18n.get('Type'),
            render: (item: DnsRecord) => (
                <Badge
                    variant={'light'}
                    // NS records are the delegation, and the ticket calls out
                    // making that visible. A distinct colour means an operator
                    // filtering to NS sees a visually coherent block rather
                    // than having to read every row's type to confirm.
                    color={item.type === 'NS' ? 'grape' : 'blue'}
                >
                    {item.type}
                </Badge>
            ),
        },
        {
            accessor: 'ttlSeconds',
            sortable: true,
            title: I18n.get('TTL'),
            // ⚠️ NULL RENDERS AS AN EM DASH, NEVER AS 0. A pointer record has no
            // TTL of its own — the provider resolves the pointer and answers
            // with the TARGET's TTL — so 0 here would be a number no provider
            // reported and that an operator would read as an alarmingly short
            // real TTL. See recordValue.ttlText.
            render: (item: DnsRecord) => (
                <Text size={'sm'} c={typeof item.ttlSeconds === 'number' ? undefined : 'dimmed'}>
                    {ttlText(item)}
                </Text>
            ),
        },
        {
            accessor: 'values',
            sortable: true,
            title: I18n.get('Value'),
            // ⚠️ THE CELL THIS WHOLE MODULE EXISTS FOR.
            //
            // A record pointing at another resource has no literal rdata; the
            // adapter resolves the target INTO `values`, so rendering `values`
            // renders the target. There is no separate `target` field to look
            // for and looking for one is what produces the blank cell this
            // ticket exists to prevent.
            //
            // The pointer badge is not decoration: it tells the operator WHY
            // this row has no TTL and why it cannot be edited as rdata.
            render: (item: DnsRecord) => (
                <Group gap={'xs'} wrap={'nowrap'} align={'flex-start'}>
                    <Text
                        size={'sm'}
                        // Multi-valued records join with newlines (a comma is
                        // legal inside a TXT value), so the cell must honour
                        // them rather than collapsing to one line.
                        style={{whiteSpace: 'pre-line'}}
                        // An absent value is dimmed so "No value reported"
                        // cannot be mistaken for a value that reads like a
                        // sentence.
                        c={hasReportedValue(item) ? undefined : 'dimmed'}
                        fs={hasReportedValue(item) ? undefined : 'italic'}
                    >
                        {recordValueText(item)}
                    </Text>
                    {item.isPointer && (
                        <Tooltip
                            multiline={true}
                            w={280}
                            label={I18n.get('This record points at another resource. The value shown is the resolved target, and the provider answers with the target\'s TTL rather than one of its own.')}
                        >
                            <Badge variant={'outline'} color={'cyan'} size={'sm'}>
                                {I18n.get('Alias')}
                            </Badge>
                        </Tooltip>
                    )}
                </Group>
            ),
        },
    ];

    return {
        columns,
    };
};
